"use client";
import React from "react";
import { AbsoluteFill, Sequence, useCurrentFrame, useVideoConfig, interpolate } from "remotion";
import { loadFont } from "@remotion/google-fonts/NotoSansDevanagari";
import { HOLES_DATA_URI } from "./trimbakHolesData";
import { DevaKathaLS2 } from "./DevaKathaLS2";
import { DevaKathaLS5 } from "./DevaKathaLS5";

const { fontFamily } = loadFont("normal", { weights: ["400", "700", "800", "900"], subsets: ["devanagari", "latin"] });

// Kedarnath phrase overlays — Trimbakeshwar holes treatment. Top-left, yellow-prominent,
// per-word colour + line breaks, black drop-shadow, scattered-holes mask, word-by-word drop-in.
// Times ABSOLUTE seconds on the assembled narration (no opener shift). 1280×704 @ 16fps.
const PAD = 25;
const YELLOW = "#FFD60A";
const WHITE = "#FFFFFF";
const RED = "#FF3B2E";
const SH = "0 5px 14px rgba(0,0,0,0.9), 0 2px 5px rgba(0,0,0,0.95)";
const HOLES = HOLES_DATA_URI;

const BASE_FS = 66, BIG_FS = 92, SMALL_FS = 46;
const STAGGER = 0.16, DROP = 26, DUR = 0.32;
const LH = 1.18;
const LINE_GAP = -10;

type Word = { t: string; c?: "y" | "w" | "r"; big?: boolean; small?: boolean };
export type Phrase = { lines: Word[][]; start: number; out: number; lineGap?: number; lh?: number; ls?: string };
const col = (c?: string) => (c === "w" ? WHITE : c === "r" ? RED : YELLOW);

// 16 selective phrases (Yash-approved). #1 = date "16 जून 2013" (16/2013 Latin, जून Devanagari).
// #4 बाहुबली gets extra line/letter spacing (ू matra was colliding).
export const KEDAR_PHRASES: Phrase[] = [
  { start: 0.6,   out: 4.2,   lines: [[{ t: "16", c: "w", big: true }, { t: "जून", c: "y" }, { t: "2013", c: "w", big: true }]] },
  { start: 25.2,  out: 28.4,  lines: [[{ t: "त्राहिमाम!", c: "r", big: true }]] },
  { start: 34.0,  out: 37.9,  lines: [[{ t: "एक", c: "w", big: true }, { t: "हज़ार", c: "w", big: true }, { t: "टन", c: "w", big: true }], [{ t: "३००", c: "y" }, { t: "हाथियों", c: "y" }, { t: "के", small: true }, { t: "बराबर", c: "y" }]] },
  { start: 43.7,  out: 47.0,  lineGap: 14, lh: 1.32, ls: "0.04em", lines: [[{ t: "बाहुबली", c: "y" }], [{ t: "भीम?", c: "w", big: true }]] },
  { start: 54.3,  out: 57.9,  lines: [[{ t: "जियोलॉजिकल", c: "y" }], [{ t: "कोइंसिडेंस?", c: "w", big: true }]] },
  { start: 64.7,  out: 68.0,  lines: [[{ t: "शिव", c: "y" }, { t: "पुराण", c: "w", big: true }]] },
  { start: 124.2, out: 127.6, lines: [[{ t: "तथास्तु", c: "y", big: true }]] },
  { start: 179.9, out: 183.2, lines: [[{ t: "इंसानी", c: "r" }], [{ t: "छेड़छाड़", c: "r", big: true }]] },
  { start: 189.5, out: 192.8, lines: [[{ t: "धारी", c: "y" }, { t: "देवी", c: "w", big: true }]] },
  { start: 215.5, out: 218.8, lines: [[{ t: "चोराबारी", c: "y" }], [{ t: "झील", c: "w", big: true }]] },
  { start: 266.6, out: 269.9, lines: [[{ t: "डेब्रिस", c: "r" }, { t: "फ्लो", c: "r", big: true }]] },
  { start: 283.5, out: 286.8, lines: [[{ t: "पच्चीस", c: "w", big: true }, { t: "फुट", c: "w", big: true }], [{ t: "की", small: true }, { t: "चट्टान", c: "y" }]] },
  { start: 309.6, out: 312.9, lines: [[{ t: "भीम", c: "y" }, { t: "शिला", c: "w", big: true }]] },
  { start: 386.3, out: 389.9, lines: [[{ t: "चार", c: "w", big: true }, { t: "सौ", c: "w", big: true }, { t: "साल", c: "w", big: true }], [{ t: "बर्फ़", c: "y" }, { t: "में", small: true }, { t: "दबा", c: "y" }]] },
  { start: 402.8, out: 406.1, lines: [[{ t: "इंटरलॉकिंग", c: "y" }], [{ t: "स्टोन", c: "w", big: true }]] },
  { start: 550.7, out: 554.2, lines: [[{ t: "भविष्य", c: "y" }], [{ t: "केदार", c: "w", big: true }]] },
];

const PhraseBlock: React.FC<{ p: Phrase; fps: number }> = ({ p, fps }) => {
  const frame = useCurrentFrame();
  const t = frame / fps;
  const phraseOp = interpolate(t, [p.out - 0.4, p.out], [1, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  if (t < p.start - 0.1 || t > p.out + 0.1) return null;
  const gap = p.lineGap ?? LINE_GAP;
  const lh = p.lh ?? LH;
  let gi = -1;
  return (
    <div style={{ position: "absolute", top: PAD, left: PAD, fontFamily, opacity: phraseOp, display: "flex", flexDirection: "column" }}>
      {p.lines.map((line, li) => (
        <div key={li} style={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: "0 16px", marginTop: li === 0 ? 0 : gap }}>
          {line.map((w, wi) => {
            gi += 1;
            const aT = p.start + gi * STAGGER;
            const k = Math.min(Math.max((t - aT) / DUR, 0), 1);
            const ease = 1 - Math.pow(1 - k, 3);
            const op = interpolate(t, [aT, aT + 0.18], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
            const y = -DROP * (1 - ease);
            return (
              <span key={wi} style={{
                display: "inline-block",
                transform: `translateY(${y}px)`,
                opacity: op,
                color: col(w.c),
                fontSize: w.big ? BIG_FS : w.small ? SMALL_FS : BASE_FS,
                fontWeight: w.big ? 900 : 800,
                lineHeight: lh,
                letterSpacing: p.ls ?? "0.005em",
                textShadow: SH,
              }}>{w.t}</span>
            );
          })}
        </div>
      ))}
    </div>
  );
};

// Bottom-left viewing tip — half the size of the date (46 ≈ BIG/2), white, NO holes mask.
const WatchTag: React.FC<{ fps: number; start: number; out: number }> = ({ fps, start, out }) => {
  const t = useCurrentFrame() / fps;
  if (t < start - 0.1 || t > out + 0.1) return null;
  const op = interpolate(t, [start, start + 0.4, out - 0.4, out], [0, 1, 1, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <div style={{ position: "absolute", bottom: PAD + 6, left: PAD, fontFamily, opacity: op }}>
      <span style={{ color: WHITE, fontSize: SMALL_FS, fontWeight: 700, letterSpacing: "0.01em", textShadow: SH }}>
        Watch at 1.2x for best experience
      </span>
    </div>
  );
};

// Center "हर हर महादेव" chant card — double-quoted Devanagari, gold, gentle fade + pulse. NO holes.
const HarHarCard: React.FC<{ fps: number; start: number; out: number }> = ({ fps, start, out }) => {
  const t = useCurrentFrame() / fps;
  if (t < start - 0.1 || t > out + 0.1) return null;
  const op = interpolate(t, [start, start + 0.45, out - 0.5, out], [0, 1, 1, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const sc = interpolate(t, [start, start + 0.6], [0.9, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const pulse = 1 + 0.02 * Math.sin((t - start) * 3.2);
  return (
    <AbsoluteFill style={{ alignItems: "center", justifyContent: "center" }}>
      <span style={{
        fontFamily, color: YELLOW, fontSize: 84, fontWeight: 900, letterSpacing: "0.01em",
        textShadow: "0 6px 18px rgba(0,0,0,0.92), 0 2px 6px rgba(0,0,0,0.95)",
        opacity: op, transform: `scale(${sc * pulse})`,
      }}>“हर हर महादेव”</span>
    </AbsoluteFill>
  );
};

// Master comp — holes-masked devotional phrases + non-masked UI (watch tip, har-har card, LS pills).
export const KedarnathOverlaysFull: React.FC<{ phrases?: Phrase[] }> = ({ phrases = KEDAR_PHRASES }) => {
  const { fps } = useVideoConfig();
  const S = (s: number) => Math.round(s * fps);
  return (
    <AbsoluteFill>
      {/* holes distress mask over the dramatic top-left phrases only */}
      <AbsoluteFill style={{
        backgroundColor: "transparent",
        WebkitMaskImage: `url(${HOLES})`, maskImage: `url(${HOLES})`,
        WebkitMaskSize: "1280px 704px", maskSize: "1280px 704px",
        WebkitMaskRepeat: "no-repeat", maskRepeat: "no-repeat",
        WebkitMaskPosition: "0 0", maskPosition: "0 0",
      }}>
        {phrases.map((p, i) => <PhraseBlock key={i} p={p} fps={fps} />)}
      </AbsoluteFill>

      {/* viewing tip (bottom-left, half-size), shows over the opening date */}
      <WatchTag fps={fps} start={1.0} out={6.5} />

      {/* "हर हर महादेव" center card @ the spoken line (82.4s) */}
      <HarHarCard fps={fps} start={82.0} out={87.0} />

      {/* Like+Subscribe — LS2 (bell) @ "सब्सक्राइब" 78.38s · LS5 @ outro "सब्सक्राइब" 595.35s */}
      <Sequence from={S(78.0)} durationInFrames={S(5.2)}><DevaKathaLS2 /></Sequence>
      <Sequence from={S(595.0)} durationInFrames={S(5.2)}><DevaKathaLS5 /></Sequence>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
